import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AssistantAction } from '../../../core/services/voice-actions/models';
import { readActionPayloadValue } from '../../../core/services/voice-actions/voice-action-payload';

const SOCIAL_POST_TREND_DRAFT_KEY = 'trend-radar:social-post:draft';
type SocialPostCreationMethod = 'upload' | 'generate';
type SocialPostDraftSource = 'voice-assistant' | 'trend-radar';

@Injectable({
  providedIn: 'root'
})
export class SocialPostVoiceActionService {
  private readonly router = inject(Router);

  async openCreate(action: AssistantAction): Promise<void> {
    const topic = readActionPayloadValue<string>(action, 'topic') || readActionPayloadValue<string>(action, 'inputData');
    const method = this.normalizeMethod(
      readActionPayloadValue<string>(action, 'method')
      || readActionPayloadValue<string>(action, 'mode')
      || readActionPayloadValue<string>(action, 'sourceMode')
    );
    const source: SocialPostDraftSource =
      readActionPayloadValue<string>(action, 'source') === 'trend-radar'
        ? 'trend-radar'
        : 'voice-assistant';

    this.persistDraft(topic, method, source);
    await this.router.navigate(['/social', 'island'], {
      queryParams: {
        create: 'post',
        source,
        voiceAction: Date.now()
      }
    });
  }

  private persistDraft(
    topic: string | undefined,
    method: SocialPostCreationMethod | undefined,
    source: SocialPostDraftSource,
  ): void {
    if (!topic?.trim() && !method) return;

    try {
      sessionStorage.setItem(SOCIAL_POST_TREND_DRAFT_KEY, JSON.stringify({
        ...(topic?.trim() ? { topic: topic.trim() } : {}),
        ...(method ? { method } : {}),
        source,
        createdAt: new Date().toISOString()
      }));
    } catch (error) {
      console.warn('Unable to persist social post draft from voice action:', error);
    }
  }

  private normalizeMethod(value?: string): SocialPostCreationMethod | undefined {
    const normalized = value?.trim().toLowerCase();
    if (!normalized) return undefined;
    if (normalized.includes('upload') || normalized.includes('file')) return 'upload';
    if (normalized.includes('ai') || normalized.includes('generate') || normalized.includes('stock')) return 'generate';
    return undefined;
  }
}
